import type { MentionStats } from '@fluenceur/types';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import React, { createContext, useContext, useMemo, useRef } from 'react';
import {
  MENTION_STATS_MAX_IDS_PER_REQUEST,
  mentionStatsQueryOptions,
} from '@/hooks/queries/use-mention-stats-query';
import { chunk } from '@/lib/chunk';

interface MentionStatsContextValue {
  /** Resolved stats for the listed mentions, keyed by mention id. */
  statsById: Map<string, MentionStats>;
  /** True while any batch is in flight (initial load or a page refetch). */
  isFetching: boolean;
}

// Neutral default so a card rendered outside a provider degrades to placeholders
// rather than crashing (never happens in the grid, where the provider is always
// present).
const MentionStatsContext = createContext<MentionStatsContextValue>({
  statsById: new Map(),
  isFetching: false,
});

interface MentionStatsProviderProps {
  /** Every mention id loaded in the grid, in display order. */
  mentionIds: string[];
  children: React.ReactNode;
}

// Module-level so its identity is stable: useQueries then only recombines when
// a batch result actually changes.
const combineBatches = (results: UseQueryResult<MentionStats[], Error>[]) => ({
  rows: results.flatMap((result) => result.data ?? []),
  isFetching: results.some((result) => result.isFetching),
});

/**
 * Fetches stats for the listed mentions in batches of at most 20 ids (the
 * backend's limit) and shares the resolved slices with the cards below, so a
 * card never fetches its own stats.
 *
 * Figures already received stay on screen while the tail batch refetches under
 * its grown key: the tail's new query starts empty, so the provider keeps what
 * it has seen instead of flashing those cards back to skeletons.
 */
export const MentionStatsProvider: React.FC<MentionStatsProviderProps> = ({
  mentionIds,
  children,
}) => {
  // Batches by position in the list: batch k is always ids[20k, 20k + 20). An
  // infinite-scroll page only appends, so every full batch keeps its query key
  // and its cache entry; only the tail batch changes, until it fills up.
  const batches = useMemo(() => chunk(mentionIds, MENTION_STATS_MAX_IDS_PER_REQUEST), [mentionIds]);
  const { rows, isFetching } = useQueries({
    queries: batches.map((ids) => mentionStatsQueryOptions(ids)),
    combine: combineBatches,
  });

  // A failed batch leaves its cards on their `--` placeholder rather than a
  // per-card error/retry; the other batches are unaffected.
  const seenStatsRef = useRef<Map<string, MentionStats>>(new Map());
  const value = useMemo<MentionStatsContextValue>(() => {
    const statsById = new Map(seenStatsRef.current);
    for (const stats of rows) {
      statsById.set(stats.id, stats);
    }
    seenStatsRef.current = statsById;
    return { statsById, isFetching };
  }, [rows, isFetching]);

  return <MentionStatsContext.Provider value={value}>{children}</MentionStatsContext.Provider>;
};

/**
 * Reads a single mention's stats from the batched grid queries. A card reports
 * loading only while a batch is in flight AND its own slice has not resolved,
 * so previously loaded cards stay put when the next page fetches.
 */
export const useMentionCardStats = (
  mentionId: string
): { stats: MentionStats | undefined; isLoading: boolean } => {
  const { statsById, isFetching } = useContext(MentionStatsContext);
  return {
    stats: statsById.get(mentionId),
    isLoading: isFetching && !statsById.has(mentionId),
  };
};
